import { actionMotionStyles } from "../../../ui/components/common/micro-interactions/actionMotion";
import { alpha, darken } from "@mui/material/styles";
import { buttonClasses } from "@mui/material/Button";
import { pressFeedback } from "./interaction";
// ----------------------------------------------------------------------
const COLORS = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
];
// ----------------------------------------------------------------------
export function button(theme, quietMotion = true) {
    const lightMode = theme.palette.mode === "light";
    const rootStyles = (ownerState) => {
        const inheritColor = ownerState.color === "inherit";
        const containedVariant = ownerState.variant === "contained";
        const outlinedVariant = ownerState.variant === "outlined";
        const textVariant = ownerState.variant === "text";
        const softVariant = ownerState.variant === "soft";
        const smallSize = ownerState.size === "small";
        const mediumSize = ownerState.size === "medium";
        const largeSize = ownerState.size === "large";
        const defaultStyle = {
            // Keep the Rebels pink; slightly deepen only actionable button surfaces
            // so small white labels meet 4.5:1, without changing the brand palette.
            ...(ownerState.color === "primary" && containedVariant && {
                backgroundColor: darken(theme.palette.primary.main, 0.04),
            }),
            ...(ownerState.color === "primary" && (textVariant || outlinedVariant) && {
                color: darken(theme.palette.primary.main, 0.08),
            }),
            ...(inheritColor && {
                // CONTAINED
                ...(containedVariant && {
                    color: lightMode
                        ? theme.palette.common.white
                        : theme.palette.grey[800],
                    backgroundColor: lightMode
                        ? theme.palette.grey[800]
                        : theme.palette.common.white,
                    "&:hover": {
                        backgroundColor: lightMode
                            ? theme.palette.grey[700]
                            : theme.palette.grey[400],
                    },
                }),
                // OUTLINED
                ...(outlinedVariant && {
                    borderColor: alpha(theme.palette.grey[500], 0.32),
                    "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                    },
                }),
                // TEXT
                ...(textVariant && {
                    "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                    },
                }),
                // SOFT
                ...(softVariant && {
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.grey[500], 0.08),
                    "&:hover": {
                        backgroundColor: alpha(theme.palette.grey[500], 0.24),
                    },
                }),
            }),
            ...(outlinedVariant && {
                "&:hover": {
                    borderColor: "currentColor",
                    boxShadow: "0 0 0 0.5px currentColor",
                },
            }),
        };
        const colorStyle = COLORS.map((color) => ({
            ...(ownerState.color === color && {
                // CONTAINED
                ...(containedVariant && {
                    "&:hover": {
                        boxShadow: theme.shadows[2],
                    },
                }),
                // SOFT
                ...(softVariant && {
                    color: theme.palette[color][lightMode ? "dark" : "light"],
                    backgroundColor: alpha(theme.palette[color].main, 0.16),
                    "&:hover": {
                        backgroundColor: alpha(theme.palette[color].main, 0.32),
                    },
                }),
            }),
        }));
        const disabledState = {
            [`&.${buttonClasses.disabled}`]: {
                // SOFT
                ...(softVariant && {
                    backgroundColor: theme.palette.action.disabledBackground,
                }),
            },
        };
        const size = {
            ...(smallSize && {
                height: 30,
                fontSize: 13,
                paddingLeft: 8,
                paddingRight: 8,
                ...(textVariant && {
                    paddingLeft: 4,
                    paddingRight: 4,
                }),
            }),
            ...(mediumSize && {
                paddingLeft: 12,
                paddingRight: 12,
                ...(textVariant && {
                    paddingLeft: 8,
                    paddingRight: 8,
                }),
            }),
            ...(largeSize && {
                height: 48,
                fontSize: 15,
                paddingLeft: 16,
                paddingRight: 16,
                ...(textVariant && {
                    paddingLeft: 10,
                    paddingRight: 10,
                }),
            }),
        };
        const borderRadius = {
            borderRadius: 50,
        };
        const motion = ownerState.color !== 'error' && ownerState.color !== 'warning' ? {
            '&[data-action-motion="true"]': {
                ...actionMotionStyles,
                // Keep the existing keyboard/reduced-motion preference and replace only this
                // opted-in action's old press scale with the bounded translation feedback.
                ...(quietMotion && {
                    '@media (prefers-reduced-motion: no-preference) and (hover: hover) and (pointer: fine)': {
                        transition: 'none',
                        '&:not(:disabled):not(.Mui-disabled):not(:focus-visible):hover': { transform: 'none' },
                        '&:not(:disabled):not(.Mui-disabled):not(:focus-visible):active': { transform: 'none' }
                    }
                }),
                '&:active:not(.Mui-disabled):not(.Mui-focusVisible):not(:focus-visible)': { transform: 'translateY(0)' },
                '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                    '&:hover, &:active:not(.Mui-disabled):not(.Mui-focusVisible):not(:focus-visible)': { transform: 'none' }
                }
            }
        } : {};
        return [defaultStyle, ...colorStyle, disabledState, size, borderRadius, pressFeedback(theme, quietMotion), motion];
    };
    return {
        MuiButton: {
            styleOverrides: {
                root: ({ ownerState }) => rootStyles(ownerState),
            },
        },
    };
}
